import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, BookOpen, Check, Zap, Loader2, Sparkles, X, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUBJECTS = [
  { id: 'bahasa-melayu', label: 'Bahasa Melayu' },
  { id: 'english', label: 'English' },
  { id: 'sains', label: 'Sains / Science' },
  { id: 'sejarah', label: 'Sejarah / History' },
  { id: 'geografi', label: 'Geografi / Geography' },
  { id: 'informatik', label: 'Sains Komputer / IT' },
  { id: 'biologi', label: 'Biologi / Biology' },
];

export default function CustomQuestionsPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filterSubject, setFilterSubject] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const isEN = language === 'en';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // Form state
  const [form, setForm] = useState({
    subject: 'sains', difficulty: 'easy',
    question_en: '', question_bm: '',
    options_en: ['', '', '', ''], options_bm: ['', '', '', ''],
    answer: 0,
  });

  // AI Generation state
  const [showAI, setShowAI] = useState(false);
  const [aiSubject, setAiSubject] = useState('sains');
  const [aiDifficulty, setAiDifficulty] = useState('easy');
  const [aiTopic, setAiTopic] = useState('');
  const [aiCount, setAiCount] = useState(10);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiQuestions, setAiQuestions] = useState([]); // generated, pending review
  const [aiSaving, setAiSaving] = useState(false);

  useEffect(() => { if (!token) { navigate('/login'); return; } fetchQuestions(); }, [token, filterSubject]);

  const fetchQuestions = async () => {
    try {
      const url = filterSubject ? `${BACKEND_URL}/api/custom-questions?subject=${filterSubject}` : `${BACKEND_URL}/api/custom-questions`;
      const res = await fetch(url, { headers });
      if (res.ok) setQuestions(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updateOption = (lang, index, value) => {
    const key = `options_${lang}`;
    const updated = [...form[key]];
    updated[index] = value;
    setForm({ ...form, [key]: updated });
  };

  const saveQuestion = async () => {
    if (!form.question_en || !form.question_bm || form.options_en.some(o => !o) || form.options_bm.some(o => !o)) {
      alert(isEN ? 'Please fill all fields' : 'Sila isi semua ruangan');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/custom-questions`, { method: 'POST', headers, body: JSON.stringify(form) });
      if (res.ok) {
        setSuccess(isEN ? 'Question saved!' : 'Soalan disimpan!');
        setTimeout(() => setSuccess(''), 2000);
        setForm({ subject: form.subject, difficulty: form.difficulty, question_en: '', question_bm: '', options_en: ['', '', '', ''], options_bm: ['', '', '', ''], answer: 0 });
        fetchQuestions();
      }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm(isEN ? 'Delete this question?' : 'Padam soalan ini?')) return;
    await fetch(`${BACKEND_URL}/api/custom-questions/${id}`, { method: 'DELETE', headers });
    fetchQuestions();
  };

  // AI Question Generation
  const generateAI = async () => {
    setAiGenerating(true);
    setAiQuestions([]);
    try {
      const res = await fetch(`${BACKEND_URL}/api/questions/generate`, {
        method: 'POST', headers,
        body: JSON.stringify({ subject: aiSubject, topic: aiTopic, difficulty: aiDifficulty, count: aiCount, language: 'both' })
      });
      if (res.ok) {
        const data = await res.json();
        // Mark all as selected by default
        setAiQuestions((data.questions || []).map((q, i) => ({ ...q, selected: true, id: i })));
      } else {
        const e = await res.json();
        setSuccess('');
        alert(e.detail || 'AI generation failed');
      }
    } catch (e) { alert('Error generating questions'); }
    setAiGenerating(false);
  };

  const toggleAiQuestion = (idx) => {
    setAiQuestions(prev => prev.map((q, i) => i === idx ? { ...q, selected: !q.selected } : q));
  };

  const saveAiQuestions = async () => {
    const selected = aiQuestions.filter(q => q.selected);
    if (selected.length === 0) return;
    setAiSaving(true);
    try {
      const bulk = selected.map(q => ({
        subject: aiSubject,
        difficulty: aiDifficulty,
        question_en: q.q_en,
        question_bm: q.q_bm,
        options_en: q.options,
        options_bm: q.options, // AI generates same options for both
        answer: q.answer,
      }));
      const res = await fetch(`${BACKEND_URL}/api/custom-questions/bulk`, {
        method: 'POST', headers,
        body: JSON.stringify({ questions: bulk })
      });
      if (res.ok) {
        setSuccess(isEN ? `${selected.length} questions saved!` : `${selected.length} soalan disimpan!`);
        setTimeout(() => setSuccess(''), 3000);
        setAiQuestions([]);
        setShowAI(false);
        fetchQuestions();
      }
    } catch (e) { alert('Error saving'); }
    setAiSaving(false);
  };

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
        </button>
        <h1 className="dashboard-header-title">{isEN ? 'Custom Questions' : 'Soalan Tersuai'}</h1>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        <div className="dashboard-intro">
          <h2 className="dashboard-title">{isEN ? 'Create Your Own Questions' : 'Buat Soalan Sendiri'}</h2>
          <p className="dashboard-desc">{isEN ? 'Add custom questions for your students. These will appear in games alongside default questions.' : 'Tambah soalan tersuai untuk murid anda. Ini akan muncul dalam permainan bersama soalan asal.'}</p>
        </div>

        {/* Filter + Add */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filterSubject} onChange={e => setFilterSubject(e.target.value)}
            style={{ padding: '10px 14px', borderRadius: '12px', border: '2px solid #e5e7eb', fontWeight: 700, fontSize: '14px' }}>
            <option value="">{isEN ? 'All Subjects' : 'Semua Subjek'}</option>
            {SUBJECTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <button className="class-create-btn" style={{ margin: 0 }} onClick={() => setShowAdd(!showAdd)}>
            <Plus size={18} /> {isEN ? 'Add Manual' : 'Tambah Manual'}
          </button>
          <button className="class-create-btn cq-ai-btn" style={{ margin: 0 }} onClick={() => { setShowAI(!showAI); setShowAdd(false); }}>
            <Zap size={18} /> {isEN ? 'Generate AI' : 'Jana AI'}
          </button>
          <span style={{ fontSize: '14px', color: '#666', fontWeight: 700 }}>{questions.length} {isEN ? 'questions' : 'soalan'}</span>
        </div>

        {/* Success */}
        {success && <div style={{ background: '#dcfce7', color: '#166534', padding: '10px 16px', borderRadius: '12px', fontWeight: 700, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} /> {success}</div>}

        {/* AI Generate Panel */}
        <AnimatePresence>
          {showAI && (
            <motion.div className="cq-ai-panel" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              data-testid="ai-generate-panel">
              <div className="cq-ai-header">
                <Sparkles size={20} color="#8b5cf6" />
                <h3>{isEN ? 'AI Question Generator' : 'Penjana Soalan AI'}</h3>
                <button className="cq-ai-close" onClick={() => setShowAI(false)}><X size={18} /></button>
              </div>
              <p className="cq-ai-desc">{isEN ? 'AI will generate bilingual MCQ questions (EN + BM) instantly!' : 'AI akan jana soalan MCQ dwibahasa (EN + BM) secara automatik!'}</p>

              {/* AI Settings */}
              <div className="cq-ai-settings">
                <div className="cq-ai-field">
                  <label>{isEN ? 'Subject' : 'Subjek'}</label>
                  <select value={aiSubject} onChange={e => setAiSubject(e.target.value)} data-testid="ai-subject">
                    {SUBJECTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                    <option value="matematik">Matematik</option>
                  </select>
                </div>
                <div className="cq-ai-field">
                  <label>{isEN ? 'Difficulty' : 'Tahap'}</label>
                  <select value={aiDifficulty} onChange={e => setAiDifficulty(e.target.value)} data-testid="ai-difficulty">
                    <option value="easy">{isEN ? 'Easy (Std 1-3)' : 'Mudah (Drjh 1-3)'}</option>
                    <option value="medium">{isEN ? 'Medium (Std 4-6)' : 'Sederhana (Drjh 4-6)'}</option>
                    <option value="hard">{isEN ? 'Hard (Form 1-3)' : 'Sukar (Ting 1-3)'}</option>
                  </select>
                </div>
                <div className="cq-ai-field">
                  <label>{isEN ? 'Topic (optional)' : 'Topik (pilihan)'}</label>
                  <input value={aiTopic} onChange={e => setAiTopic(e.target.value)}
                    placeholder={isEN ? 'e.g. Fractions, Photosynthesis' : 'cth. Pecahan, Fotosintesis'} data-testid="ai-topic" />
                </div>
                <div className="cq-ai-field">
                  <label>{isEN ? 'Questions' : 'Soalan'}</label>
                  <select value={aiCount} onChange={e => setAiCount(Number(e.target.value))} data-testid="ai-count">
                    {[5, 10, 15, 20].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>

              <button className="cq-ai-generate-btn" onClick={generateAI} disabled={aiGenerating} data-testid="ai-generate-btn">
                {aiGenerating ? <><Loader2 size={18} className="spin" /> {isEN ? 'Generating...' : 'Menjana...'}</> : <><Zap size={18} /> {isEN ? 'Generate Questions' : 'Jana Soalan'}</>}
              </button>

              {/* AI Generated Questions - Review */}
              {aiQuestions.length > 0 && (
                <div className="cq-ai-results" data-testid="ai-results">
                  <div className="cq-ai-results-header">
                    <h4>{aiQuestions.length} {isEN ? 'questions generated' : 'soalan dijana'}</h4>
                    <span className="cq-ai-selected">{aiQuestions.filter(q => q.selected).length} {isEN ? 'selected' : 'dipilih'}</span>
                  </div>
                  <p className="cq-ai-review-hint">{isEN ? 'Review and uncheck any questions you don\'t want. Then save.' : 'Semak dan nyahtanda soalan yang tidak mahu. Kemudian simpan.'}</p>

                  <div className="cq-ai-list">
                    {aiQuestions.map((q, i) => (
                      <div key={i} className={`cq-ai-item ${q.selected ? 'cq-ai-item-selected' : 'cq-ai-item-deselected'}`}
                        onClick={() => toggleAiQuestion(i)} data-testid={`ai-question-${i}`}>
                        <div className="cq-ai-item-check">
                          {q.selected ? <Check size={16} /> : <span className="cq-ai-item-uncheck" />}
                        </div>
                        <div className="cq-ai-item-content">
                          <div className="cq-ai-item-q">{isEN ? q.q_en : (q.q_bm || q.q_en)}</div>
                          <div className="cq-ai-item-opts">
                            {(q.options || []).map((opt, j) => (
                              <span key={j} className={`cq-ai-item-opt ${j === q.answer ? 'cq-ai-item-opt-correct' : ''}`}>
                                {String.fromCharCode(65 + j)}: {opt}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button className="cq-ai-save-btn" onClick={saveAiQuestions} disabled={aiSaving || aiQuestions.filter(q => q.selected).length === 0}
                    data-testid="ai-save-btn">
                    {aiSaving ? <><Loader2 size={16} className="spin" /> {isEN ? 'Saving...' : 'Menyimpan...'}</> : <><Save size={16} /> {isEN ? `Save ${aiQuestions.filter(q => q.selected).length} Questions` : `Simpan ${aiQuestions.filter(q => q.selected).length} Soalan`}</>}
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Add Form (Manual) */}
        {showAdd && (
          <motion.div className="cq-form" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="cq-form-row">
              <div className="cq-form-field">
                <label>{isEN ? 'Subject' : 'Subjek'}</label>
                <select value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}>
                  {SUBJECTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
              <div className="cq-form-field">
                <label>{isEN ? 'Difficulty' : 'Tahap'}</label>
                <select value={form.difficulty} onChange={e => setForm({ ...form, difficulty: e.target.value })}>
                  <option value="easy">{isEN ? 'Easy (Primary 1-3)' : 'Mudah (Drjh 1-3)'}</option>
                  <option value="medium">{isEN ? 'Medium (Primary 4-6)' : 'Sederhana (Drjh 4-6)'}</option>
                  <option value="hard">{isEN ? 'Hard (Secondary)' : 'Sukar (Menengah)'}</option>
                </select>
              </div>
            </div>

            <div className="cq-form-row">
              <div className="cq-form-field"><label>{isEN ? 'Question (English)' : 'Soalan (English)'}</label>
                <input value={form.question_en} onChange={e => setForm({ ...form, question_en: e.target.value })} placeholder="What is the capital of Japan?" />
              </div>
              <div className="cq-form-field"><label>{isEN ? 'Question (BM)' : 'Soalan (BM)'}</label>
                <input value={form.question_bm} onChange={e => setForm({ ...form, question_bm: e.target.value })} placeholder="Apakah ibu negara Jepun?" />
              </div>
            </div>

            <div className="cq-options-grid">
              {[0, 1, 2, 3].map(i => (
                <div key={i} className={`cq-option-card ${form.answer === i ? 'cq-option-correct' : ''}`} onClick={() => setForm({ ...form, answer: i })}>
                  <div className="cq-option-letter">{String.fromCharCode(65 + i)}</div>
                  <input placeholder={`Option ${i + 1} (EN)`} value={form.options_en[i]} onChange={e => updateOption('en', i, e.target.value)} onClick={e => e.stopPropagation()} />
                  <input placeholder={`Pilihan ${i + 1} (BM)`} value={form.options_bm[i]} onChange={e => updateOption('bm', i, e.target.value)} onClick={e => e.stopPropagation()} />
                  {form.answer === i && <div className="cq-option-check"><Check size={14} /></div>}
                </div>
              ))}
            </div>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{isEN ? 'Click on the correct answer option (green = correct)' : 'Klik pada pilihan jawapan yang betul (hijau = betul)'}</p>

            <button className="auth-submit" style={{ marginTop: '12px', maxWidth: '300px' }} onClick={saveQuestion} disabled={saving}>
              {saving ? '...' : isEN ? 'Save Question' : 'Simpan Soalan'}
            </button>
          </motion.div>
        )}

        {/* Questions List */}
        {loading ? <div className="lb-loading"><div className="lb-spinner" /></div> : questions.length === 0 ? (
          <div className="lb-empty">{isEN ? 'No custom questions yet. Add your first question!' : 'Tiada soalan tersuai. Tambah soalan pertama anda!'}</div>
        ) : (
          <div className="cq-list">
            {questions.map((q) => (
              <div key={q._id} className="cq-card">
                <div className="cq-card-header">
                  <span className="cq-card-subject">{q.subject}</span>
                  <span className="cq-card-difficulty">{q.difficulty}</span>
                  <button className="class-remove-btn" onClick={() => deleteQuestion(q._id)}><Trash2 size={14} /></button>
                </div>
                <div className="cq-card-question">
                  <div><strong>EN:</strong> {q.question_en}</div>
                  <div><strong>BM:</strong> {q.question_bm}</div>
                </div>
                <div className="cq-card-options">
                  {q.options_en.map((opt, i) => (
                    <span key={i} className={`cq-card-opt ${i === q.answer ? 'cq-card-opt-correct' : ''}`}>
                      {String.fromCharCode(65 + i)}: {opt}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <button className="setup-back-btn" onClick={() => navigate('/')} style={{ marginTop: '16px' }}>&larr; {isEN ? 'Back' : 'Kembali'}</button>
      </div>
    </div>
  );
}
